import React from 'react';
import {
  useNotifications, FormHandlers, useModal, Action, Portal,
} from '@doveit/bricks';
import { JobApplication } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import UpdateSupervisorForm, { UpdateSupervisorFormModel } from '../../components/update-supervisor-form/UpdateSupervisorForm';
import { updateJobApplication } from '../../../providers/api/job-application/jobApplicationProvider';
import { jobApplicationHrUsersOptions, jobApplicationHrUsers } from '../../constants';

export const UPDATE_SUPERVISOR_SUCCESS_MESSAGE = 'Il gestore HR è stato sostituito con successo';
export const UPDATE_SUPERVISOR_ERROR_MESSAGE = 'Non è stato possibile sostituire il gestore HR';

interface ChildFnProps {
  openUpdateModal: VoidFunction,
  isSaving: boolean,
}

interface UpdateSupervisorActionBaseProps extends React.AriaAttributes {
  jobApplication: JobApplication,
  children?: (props: ChildFnProps) => React.ReactNode,
  onSuccess?: (jobApplication: JobApplication) => void,
}

export type UpdateSupervisorActionProps = UpdateSupervisorActionBaseProps;

const UpdateSupervisorAction: React.FC<UpdateSupervisorActionProps> = ({
  jobApplication,
  children,
  onSuccess,
  ...rest
}) => {
  const modal = useModal();
  const formRef = React.useRef<FormHandlers>(null) as React.MutableRefObject<FormHandlers>;
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const currentSupervisor = React.useMemo(() => (jobApplication?.supervisorEmail ? jobApplicationHrUsers.filter((supervisor) => supervisor.email === jobApplication?.supervisorEmail)[0] : undefined), [jobApplication.supervisorEmail]);

  const handleSubmit = React.useCallback(async (values: UpdateSupervisorFormModel) => {
    try {
      setIsSaving(true);

      const updatedReference = await updateJobApplication(jobApplication.id!, {
        ...jobApplication,
        supervisorEmail: values.supervisor,
      });

      setIsSaving(false);
      modal.close();
      addSuccess(UPDATE_SUPERVISOR_SUCCESS_MESSAGE);

      onSuccess?.(updatedReference);
    } catch (err) {
      setIsSaving(false);
      addError(UPDATE_SUPERVISOR_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, jobApplication, modal, onSuccess]);

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  return (
    <>
      {children ? children({
        openUpdateModal: modal.open,
        isSaving,
      }) : (
        <Action
          label={`${jobApplication?.supervisorEmail ? 'Sostituisci' : 'Aggiungi'} gestore HR`}
          aria-label={`Azione per ${jobApplication?.supervisorEmail ? 'sostituire' : 'aggiungere'} il gestore HR`}
          size="S"
          loading={isSaving}
          onClick={() => modal.open()}
          {...rest}
        />
      )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`${jobApplication?.supervisorEmail ? 'Sostituisci' : 'Aggiungi'} gestore HR`}
          aria-label={`Modale per ${jobApplication?.supervisorEmail ? 'sostituire' : 'aggiungere'} il gestore HR`}
          footer={(
            <Action
              label={`${jobApplication?.supervisorEmail ? 'Sostituisci' : 'Aggiungi'}`}
              color="primary"
              emphasis="high"
              aria-label={`Azione per confermare ${jobApplication?.supervisorEmail ? 'la sostituzione' : 'l\'aggiunta'} del gestore HR`}
              onClick={submitForm}
            />
          )}
        >
          <UpdateSupervisorForm
            initialValues={{ supervisor: currentSupervisor?.email }}
            supervisors={jobApplicationHrUsersOptions}
            onSubmit={handleSubmit}
            innerRef={formRef}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpdateSupervisorAction;
